'use client'

import { useState, useEffect, useRef } from 'react'
import {
    FiChevronUp,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
} from 'react-icons/fi'
import { MdOutlineImageNotSupported } from 'react-icons/md'
import { Skeleton } from '@/components/ui'
import type { Image } from './type'

interface ProductImagesProps {
    main_image: string
    images: string[]
    alt?: string
}

export default function ProductImages({
    images,
    main_image,
    alt = 'Фото товара',
}: ProductImagesProps) {
    console.log(images, 'как выглядит массив по приходу в слайдер')
    console.log(main_image, 'главная картинка')

    const allImages = [
    ...(main_image ? [main_image] : []),
    ...images,
]
    const sliderRef = useRef<HTMLDivElement>(null)
    const startX = useRef(0)

    const [dragX, setDragX] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [selected, setSelected] = useState(0)
    const [validImages, setValidImages] = useState<(string | null)[]>([])
    const [isChecking, setIsChecking] = useState(true)

    const visibleCount = 3
    const [thumbStart, setThumbStart] = useState(0)
    const visibleThumbs = validImages.slice(
        thumbStart,
        thumbStart + visibleCount,
    )

    console.log(thumbStart, 'thumbStart данные')
    console.log(visibleThumbs, 'visibleThumbs данные')
    // Проверяем, какие картинки доступны
    useEffect(() => {
        const checkImages = async () => {
            setIsChecking(true)
            const results = await Promise.all(
                allImages.map(async (img) => {
                    try {
                        const res = await fetch(img, { method: 'HEAD' })
                        return res.ok ? img : null
                    } catch {
                        return null
                    }
                }),
            )
            setValidImages(results)
            setIsChecking(false)
        }
        checkImages()
    }, [images])

    // Навигация по миниатюрам
    const nextImage = () => {
        if (selected < validImages.length - 1) {
            const next = selected + 1
            setSelected(next)
            if (next >= thumbStart + visibleCount) setThumbStart((p) => p + 1)
        }
    }

    const prevImage = () => {
        if (selected > 0) {
            const prev = selected - 1
            setSelected(prev)
            if (prev < thumbStart) setThumbStart((p) => Math.max(0, p - 1))
        }
    }

    // Мобильный свайп
    const onTouchStart = (e: React.TouchEvent) => {
        startX.current = e.touches[0].clientX
        setIsDragging(true)
    }

    const onTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return
        const currentX = e.touches[0].clientX
        const deltaX = currentX - startX.current

        // если горизонтальное движение заметное — блокируем вертикальный скролл
        if (Math.abs(deltaX) > 5) {
            e.preventDefault()
        }

        setDragX(deltaX)
    }

    const onTouchEnd = () => {
        const width = sliderRef.current?.offsetWidth || 1
        const threshold = width * 0.18

        const swipeDistance = dragX
        const swipeSpeed = Math.abs(dragX) // можно добавить время для более точной скорости

        if (swipeDistance < -threshold) nextImage()
        else if (swipeDistance > threshold) prevImage()

        setDragX(0)
        setIsDragging(false)
    }

    if (isChecking) {
        return (
            <div className="flex flex-col md:flex-row gap-4 w-full">
                <div className="hidden md:flex flex-col items-center gap-3">
                    <Skeleton
                        className="w-[40px] h-[40px] rounded-full"
                        variant="block"
                    />
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton
                                key={i}
                                className="w-[88px] h-[88px] rounded-sm"
                                variant="block"
                            />
                        ))}
                    </div>
                    <Skeleton
                        className="w-[40px] h-[40px] rounded-full"
                        variant="block"
                    />
                </div>
                <div className="relative w-full aspect-square">
                    <Skeleton className="w-full h-full" variant="block" />
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col md:flex-row gap-4">
            {/* Миниатюры на десктопе */}
            <div className="hidden md:flex flex-col items-center gap-3 select-none">
                <button
                    onClick={prevImage}
                    disabled={selected === 0}
                    className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 w-[40px] h-[40px] shadow-soft rounded-full"
                >
                    <FiChevronUp size={20} />
                </button>

                <div className="flex flex-col gap-2 overflow-hidden relative h-[280px]">
                    {visibleThumbs.map((img, idx) => {
                        const realIndex = thumbStart + idx
                        return (
                            <button
                                key={realIndex}
                                onClick={() => setSelected(realIndex)}
                                className={`relative w-[88px] h-[88px] border rounded-sm overflow-hidden transition-all duration-200 ${
                                    selected === realIndex
                                        ? 'border-yellow-cus'
                                        : 'border-gray-200'
                                }`}
                            >
                                {img ? (
                                    <img
                                        src={`${process.env.NEXT_PUBLIC_IMG_URL}/${img}`}
                                        alt={`${alt} ${realIndex + 1}`}
                                        className="object-cover p-[8px] w-full h-full"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center w-full h-full bg-gray-100">
                                        <MdOutlineImageNotSupported className="text-gray-300 w-6 h-6" />
                                    </div>
                                )}
                            </button>
                        )
                    })}
                </div>

                <button
                    onClick={nextImage}
                    disabled={selected === validImages.length - 1}
                    className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 w-[40px] h-[40px] shadow-soft rounded-full"
                >
                    <FiChevronDown size={20} />
                </button>
            </div>

            {/* Главное изображение */}
            <div className="relative flex-1 overflow-hidden bg-white">
                <div className="relative w-full md:aspect-square h-[60vh] md:h-auto overflow-hidden">
                    {/* Десктоп */}
                    <div className="hidden md:block w-full h-full">
                        <img
                            src={`${process.env.NEXT_PUBLIC_IMG_URL}/${validImages[selected]}`}
                            alt={alt}
                            className="object-contain w-full h-full"
                        />
                    </div>

                    {/* Мобильный слайдер */}
                    <div
                        ref={sliderRef}
                        className="md:hidden overflow-hidden w-full h-full relative"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        {/* Кнопки листания */}
                        <button
                            onClick={prevImage}
                            disabled={selected === 0}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full z-10 disabled:opacity-30"
                        >
                            <FiChevronLeft size={24} />
                        </button>
                        <button
                            onClick={nextImage}
                            disabled={selected === validImages.length - 1}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full z-10 disabled:opacity-30"
                        >
                            <FiChevronRight size={24} />
                        </button>

                        <div
                            className="flex h-full"
                            style={{
                                transform: `translateX(calc(${-selected * 100}% + ${dragX}px))`,
                                transition: isDragging
                                    ? 'none'
                                    : 'transform 0.25s ease-out',
                            }}
                        >
                            {validImages.map((img, idx) => (
                                <div
                                    key={idx}
                                    className="w-full h-full shrink-0 flex items-center justify-center"
                                >
                                    {img ? (
                                        <img
                                            src={`${process.env.NEXT_PUBLIC_IMG_URL}/${img}`}
                                            alt={`${alt} ${idx + 1}`}
                                            className="object-contain w-full h-full select-none"
                                            draggable={false}
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center w-full h-full bg-gray-100">
                                            <MdOutlineImageNotSupported
                                                size={48}
                                                className="text-gray-300"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Индикаторы */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {validImages.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setSelected(idx)}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                                        selected === idx
                                            ? 'bg-white scale-125'
                                            : 'bg-gray-400'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// import { useEffect, useRef, useState } from 'react'
// import {
//     FiChevronUp,
//     FiChevronDown,
//     FiChevronLeft,
//     FiChevronRight,
// } from 'react-icons/fi'
// import { MdOutlineImageNotSupported } from 'react-icons/md'

// import type { Image } from './type'

// interface ProductImagesProps {
//     main_image?: string
//     images: Image[]
//     alt?: string
// }

// export default function ProductImages({
//     images,
//     main_image,
//     alt = 'Фото товара',
// }: ProductImagesProps) {
//     const sliderRef = useRef<HTMLDivElement>(null)
//     const startX = useRef(0)

//     const [dragX, setDragX] = useState(0)
//     const [isDragging, setIsDragging] = useState(false)
//     const [selected, setSelected] = useState(0)
//     const [thumbStart, setThumbStart] = useState(0)
//     const [isFading, setIsFading] = useState(false)
//     const [brokenIds, setBrokenIds] = useState<Set<string>>(new Set())

//     const visibleCount = 3
//     const IMG_BASE = process.env.NEXT_PUBLIC_IMG_URL

//     // если пришёл новый продукт — сбрасываем индекс
//     useEffect(() => {
//         setSelected(0)
//         setThumbStart(0)
//         setBrokenIds(new Set())
//     }, [images])

//     const visibleThumbs = images.slice(thumbStart, thumbStart + visibleCount)
//     const currentImage = images[selected]?.local_src || main_image

//     const buildSrc = (src?: string) =>{
//     if (!src) return undefined
//     if (!IMG_BASE) return undefined // <- сразу undefined, тогда renderImage покажет заглушку
//     return `${IMG_BASE}/${src}`
// }

//     // Навигация по миниатюрам
//     const nextImage = () => {
//         if (selected < images.length - 1) {
//             const next = selected + 1
//             setIsFading(true)
//             setSelected(next)
//             if (next >= thumbStart + visibleCount) setThumbStart((p) => p + 1)
//         }
//     }

//     const prevImage = () => {
//         if (selected > 0) {
//             const prev = selected - 1
//             setIsFading(true)
//             setSelected(prev)
//             if (prev < thumbStart) setThumbStart((p) => Math.max(0, p - 1))
//         }
//     }

//     // Mobile swipe
//     const onTouchStart = (e: React.TouchEvent) => {
//         startX.current = e.touches[0].clientX
//         setIsDragging(true)
//     }

//     const onTouchMove = (e: React.TouchEvent) => {
//         if (!isDragging) return
//         const deltaX = e.touches[0].clientX - startX.current
//         if (Math.abs(deltaX) > 5) e.preventDefault()
//         setDragX(deltaX)
//     }

//     const onTouchEnd = () => {
//         const width = sliderRef.current?.offsetWidth || 1
//         const threshold = width * 0.18
//         if (dragX < -threshold) nextImage()
//         else if (dragX > threshold) prevImage()
//         setDragX(0)
//         setIsDragging(false)
//     }

//     const renderImage = (src?: string, id?: string | number) => {
//         if (!src || (id && brokenIds.has(String(id)))) {
//             return (
//                 <div className="flex items-center justify-center w-full h-full bg-gray-100">
//                     <MdOutlineImageNotSupported size={48} className="text-gray-300 w-6 h-6" />
//                 </div>
//             )
//         }
//         return (
//             <img
//                 src={buildSrc(src)}
//                 alt={alt}
//                 className="object-contain w-full h-full select-none"
//                 draggable={false}
//                 onError={(e) => {
//                     if (id) setBrokenIds((prev) => new Set(prev).add(String(id)))
//                 }}
//             />
//         )
//     }

//     if (!images.length && !main_image) {
//         return renderImage(undefined)
//     }

//     return (
//         <div className="flex flex-col md:flex-row gap-4">
//             {/* Thumbnails desktop */}
//             <div className="hidden md:flex flex-col items-center gap-3 select-none">
//                 <button
//                     onClick={prevImage}
//                     disabled={selected === 0}
//                     className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 w-[40px] h-[40px] shadow-soft rounded-full"
//                 >
//                     <FiChevronUp size={20} />
//                 </button>

//                 <div className="flex flex-col gap-2 overflow-hidden relative h-[280px]">
//                     {visibleThumbs.map((img, idx) => {
//                         const realIndex = thumbStart + idx
//                         const isSelected = selected === realIndex
//                         return (
//                             <button
//                                 key={img.id}
//                                 onClick={() => {
//                                     setIsFading(true)
//                                     setSelected(realIndex)
//                                 }}
//                                 className={`
//                                     relative w-[88px] h-[88px] rounded-sm overflow-hidden
//                                     border transition-all duration-500 ease-in-out
//                                     ${isSelected ? 'border-yellow-cus shadow-md scale-[1.05]' : 'border-gray-200 scale-100'}
//                                 `}
//                             >
//                                 {renderImage(img.local_src, img.id)}
//                             </button>
//                         )
//                     })}
//                 </div>

//                 <button
//                     onClick={nextImage}
//                     disabled={selected === images.length - 1}
//                     className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 w-[40px] h-[40px] shadow-soft rounded-full"
//                 >
//                     <FiChevronDown size={20} />
//                 </button>
//             </div>

//             {/* Main image */}
//             <div className="relative flex-1 overflow-hidden bg-white">
//                 <div className="relative w-full md:aspect-square h-[60vh] md:h-auto overflow-hidden">
//                     {/* Desktop */}
//                     <div className="hidden md:block w-full h-full transition-opacity duration-300">
//                         {renderImage(currentImage, images[selected]?.id)}
//                     </div>

//                     {/* Mobile slider */}
//                     <div
//                         ref={sliderRef}
//                         className="md:hidden overflow-hidden w-full h-full relative"
//                         onTouchStart={onTouchStart}
//                         onTouchMove={onTouchMove}
//                         onTouchEnd={onTouchEnd}
//                     >
//                         <button
//                             onClick={prevImage}
//                             disabled={selected === 0}
//                             className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full z-10 disabled:opacity-30"
//                         >
//                             <FiChevronLeft size={24} />
//                         </button>

//                         <button
//                             onClick={nextImage}
//                             disabled={selected === images.length - 1}
//                             className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full z-10 disabled:opacity-30"
//                         >
//                             <FiChevronRight size={24} />
//                         </button>

//                         <div
//                             className="flex h-full"
//                             style={{
//                                 transform: `translateX(calc(${-selected * 100}% + ${dragX}px))`,
//                                 transition: isDragging ? 'none' : 'transform 0.25s ease-out',
//                             }}
//                         >
//                             {images.map((img) => (
//                                 <div
//                                     key={img.id}
//                                     className="w-full h-full shrink-0 flex items-center justify-center"
//                                 >
//                                     {renderImage(img.local_src, img.id)}
//                                 </div>
//                             ))}
//                         </div>

//                         {/* Dots */}
//                         <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
//                             {images.map((_, idx) => (
//                                 <button
//                                     key={idx}
//                                     onClick={() => setSelected(idx)}
//                                     className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
//                                         selected === idx ? 'bg-white scale-125' : 'bg-gray-400'
//                                     }`}
//                                 />
//                             ))}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     )
// }
